import styles from './error-log-dialog.module.scss';
import { Col, Row } from 'reactstrap';
import { useGetRequestNewHook } from '../requestUtils';
import { Accordion, AccordionDetails, AccordionSummary, dateFormatter, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, Icon, MultiLineTextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@frontend-workspace/react-shared-lib';
import { IDeviceInfoDifference, IErrorLog, IUnavailableLicense } from '../_models/error-log';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface ErrorLogDialogProps {
  onClose: () => void,
  errorLogId: string
}

export function ErrorLogDialog(props: ErrorLogDialogProps) {
  const { t } = useTranslation();
  const { data: errorLog }: { data: IErrorLog } = useGetRequestNewHook(`licenses/errorlogs/${props.errorLogId}`, null);
  if (!errorLog) return null;

  return (
    <Dialog open maxWidth="xl" fullWidth>
      <DialogTitle>{t("errorLog.loginFailed")}</DialogTitle>
      <DialogContent>
        <div className='d-flex flex-column'>
          <div className='mb-3'>
            <MultiLineTextField
              value={errorLog.errorMessage}
              fullWidth
              minRows={1}
              label={t("standard.errorText")}
              disabled
            />
          </div>
          <div className='mb-3'>
            <MultiLineTextField
              value={errorLog.url}
              fullWidth
              label="URL"
              minRows={1}
              disabled
            />
          </div>
        </div>
        <Row>
          <Col md={6} xxl={4}>
            <div className="d-flex flex-column mb-3">
              <TextField
                value={errorLog.personName}
                fullWidth
                label={t("standard.person")}
                className='mb-3'
              />
              <TextField
                value={errorLog.userEmail}
                fullWidth
                label={t("standard.email")}
                className='mb-3'
              />
              <TextField
                value={errorLog.userAgent}
                fullWidth
                label={t("errorLog.userAgent")}
                className='mb-3'
              />
              <TextField
                value={errorLog.licenseNumber}
                fullWidth
                label={t("errorLog.licenseNumber")}
                className='mb-3'
              />
            </div>
          </Col>
          <Col md={6} xxl={4}>
            <div className="d-flex flex-column mb-3">
              <TextField
                value={errorLog.companyName}
                fullWidth
                label={t("standard.company")}
                className='mb-3'
              />
              <TextField
                value={errorLog.companyNumber}
                fullWidth
                label={t("errorLog.companyNumber")}
                className='mb-3'
              />
              <TextField
                value={errorLog.productName}
                fullWidth
                label={t("standard.productName")}
                className='mb-3'
              />
              <TextField
                value={errorLog.productCode}
                fullWidth
                label={t("standard.productCode")}
                className='mb-3'
              />
            </div>
          </Col>
          <Col md={6} xxl={4}>
            <div className="d-flex flex-column mb-3">
              <TextField
                value={dateFormatter(errorLog.timestamp)}
                fullWidth
                label={t("standard.timestamp")}
                className='mb-3'
              />
              <TextField
                value={errorLog.errorType}
                fullWidth
                label={t("standard.errorType")}
                className='mb-3'
              />
              <TextField
                value={errorLog.function}
                fullWidth
                label={t("standard.function")}
                className='mb-3'
              />
              <TextField
                value={errorLog.remoteIpAddress}
                fullWidth
                label={t("errorLog.IPAdress")}
                className='mb-3'
              />
            </div>
          </Col>
        </Row>
        <Typography
          variant="body3"
          component="div"
          className='mb-2'>
          {errorLog.unavailableLicenses.length > 0 &&
            (errorLog.unavailableLicenses.length > 1
              ? t("errorLog.unavailableLicenses")
              : t("errorLog.unavailableLicense")
            )
          }
        </Typography>
        {errorLog.unavailableLicenses.map((ul) => (
          <Accordion>
            <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
              <Typography>{`${t("standard.licenseNumber")} ${ul.licenseNumber}`}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <LicenseRow key={ul.licenseNumber} unavailableLicense={ul} t={t} />
            </AccordionDetails>
          </Accordion>
        ))}
        {errorLog.request && <Accordion>
          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
            <Typography>{t("errorLog.request")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {Object.entries(JSON.parse(errorLog.request)).map(([key, value]: any) => (
              <div key={key}>
                <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value, null, 2) : value.toString()}
              </div>
            ))}
          </AccordionDetails>
        </Accordion>}
      </DialogContent>
      <DialogActions>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog >
  );
}

const DeviceInfoDifference: React.FC<IDeviceInfoDifference> = ({ name, oldValue, newValue }) => (
  <TableRow
    key={name}
    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
    <TableCell>{name}</TableCell>
    <TableCell>{oldValue}</TableCell>
    <TableCell>{newValue}</TableCell>
  </TableRow>
);

interface LicenseRowProps {
  unavailableLicense: IUnavailableLicense;
  t: (key: string) => string;
}

const LicenseRow: React.FC<LicenseRowProps> = ({ unavailableLicense, t }) => (<>
  <TextField
    value={unavailableLicense.errorType}
    fullWidth
    label={t("errorLog.error")}
    disabled
    className='mb-3'
  />
  <label>{t("errorLog.inequalityOnUnit")}</label>
  <TableContainer>
    <Table sx={{ width: '100%' }} size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("standard.name")}</TableCell>
          <TableCell>{t("errorLog.oldValue")}</TableCell>
          <TableCell>{t("errorLog.newValue")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {unavailableLicense.deviceInfoDifferences.map((dif) => (
          <DeviceInfoDifference key={dif.name} {...dif} />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
</>);

export default ErrorLogDialog;